import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { CardShell, EmptyState } from '../cards/card-shell';
import { formatBrl } from './format';

/**
 * Ultimas 5 transactions com status='paid', ordenadas por paymentDate desc.
 * Reusa o endpoint generico `financialTransactionsList` com filtros.
 */
export function RecentTransactionsWidget() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['financial', 'tx', 'recent-paid'],
    queryFn: () =>
      apiClient.financialTransactionsList({
        status: 'paid',
        orderBy: 'paymentDate',
        orderDir: 'desc',
        page: 1,
        pageSize: 5,
      }),
    staleTime: 1000 * 60,
  });

  const items = data?.items ?? [];

  return (
    <CardShell title="Lançamentos recentes" icon={History}>
      <div className="flex h-full w-full flex-col gap-2">
        {isLoading ? (
          <div className="flex flex-col gap-1.5">
            <div className="h-5 w-full animate-pulse rounded bg-[var(--surface-muted)]" />
            <div className="h-5 w-full animate-pulse rounded bg-[var(--surface-muted)]" />
            <div className="h-5 w-full animate-pulse rounded bg-[var(--surface-muted)]" />
          </div>
        ) : isError ? (
          <p className="text-xs text-[var(--fg-muted)]">
            Não foi possível carregar.
          </p>
        ) : items.length === 0 ? (
          <EmptyState
            icon={History}
            message="Nenhum lançamento pago ainda."
          />
        ) : (
          <ul className="flex w-full flex-col divide-y divide-[var(--border)]">
            {items.map((tx) => {
              const amount = Number(tx.amount ?? 0);
              const isIncome = tx.type === 'income';
              const isExpense = tx.type === 'expense';
              const sign = isExpense ? '-' : isIncome ? '+' : '';
              const color = isIncome
                ? 'text-emerald-500'
                : isExpense
                  ? 'text-red-500'
                  : 'text-[var(--fg)]';
              const dateIso = tx.paymentDate ?? tx.dueDate;
              const dateLabel = new Date(
                dateIso.length === 10 ? `${dateIso}T12:00:00Z` : dateIso,
              ).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
              });
              return (
                <li key={tx.id} className="flex items-center gap-2 py-1.5">
                  <span className="w-10 shrink-0 text-[10px] tabular-nums text-[var(--fg-muted)]">
                    {dateLabel}
                  </span>
                  <Link
                    to={`/escritorio/financeiro/${tx.id}`}
                    className="min-w-0 flex-1 truncate text-xs text-[var(--fg)] hover:underline"
                    title={tx.description}
                  >
                    {tx.description}
                  </Link>
                  <span
                    className={cn(
                      'shrink-0 text-xs font-medium tabular-nums',
                      color,
                    )}
                  >
                    {sign}
                    {formatBrl(amount)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </CardShell>
  );
}
